"use client";
import { createContext, useContext, type ReactNode } from "react";
import type { ApiService } from "@/api/types";

const ApiContext = createContext<ApiService | null>(null);

interface ApiProviderProps {
  service: ApiService;
  children: ReactNode;
}

/**
 * Provides the API service to all child components.
 * The service implementation (mock or real) is determined at the layout level.
 */
export function ApiProvider({ service, children }: ApiProviderProps) {
  return <ApiContext.Provider value={service}>{children}</ApiContext.Provider>;
}

/**
 * Hook to access the API service.
 * Must be used within an ApiProvider.
 *
 * @example
 * const api = useApi();
 * const questions = await api.getQuestions();
 */
export function useApi(): ApiService {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error("useApi must be used within an ApiProvider");
  }
  return context;
}
